import { useState, useEffect } from "react";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { ArrowLeft, CheckCircle, XCircle, Shield } from "lucide-react";
import { useNavigate } from "react-router-dom";
import Footer from "@/components/Footer";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const UserDashboard = () => {
  const navigate = useNavigate();
  const [licenseData, setLicenseData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [verifying, setVerifying] = useState(false);

  const [formData, setFormData] = useState({
    user_email: "",
    license_key: "",
  });

  const handleVerifyLicense = async (e) => {
    e.preventDefault();
    try {
      setVerifying(true);
      const response = await axios.post(`${API}/licenses/verify`, formData);

      if (response.data.valid) {
        toast.success("License verified successfully!");
        setLicenseData(response.data.license_data);
        // Store in localStorage for persistence
        localStorage.setItem("license_data", JSON.stringify(response.data.license_data));
        localStorage.setItem("user_email", formData.user_email);
      } else {
        toast.error(response.data.message);
        setLicenseData(null);
      }
    } catch (error) {
      toast.error("License verification failed");
      console.error(error);
    } finally {
      setVerifying(false);
    }
  };

  const handleCheckStatus = async () => {
    const email = localStorage.getItem("user_email");
    if (!email) {
      toast.error("Please verify your license first");
      return;
    }

    try {
      setLoading(true);
      const response = await axios.get(`${API}/licenses/status/${email}`);
      setLicenseData(response.data);
      localStorage.setItem("license_data", JSON.stringify(response.data));
      toast.success("License status updated");
    } catch (error) {
      if (error.response?.status === 404) {
        toast.error("No active license found");
        setLicenseData(null);
        localStorage.removeItem("license_data");
      } else {
        toast.error("Failed to fetch license status");
      }
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem("license_data");
    localStorage.removeItem("user_email");
    setLicenseData(null);
    setFormData({ user_email: "", license_key: "" });
    toast.success("Logged out successfully");
  };

  // Check for stored license on mount
  useEffect(() => {
    const storedLicense = localStorage.getItem("license_data");
    const storedEmail = localStorage.getItem("user_email");
    if (storedLicense && storedEmail) {
      setLicenseData(JSON.parse(storedLicense));
      setFormData({ ...formData, user_email: storedEmail });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const getStatusBadgeClass = (status) => {
    const classes = {
      active: "badge-active",
      inactive: "badge-inactive",
      expired: "badge-expired",
    };
    return `badge ${classes[status] || ""}`;
  };

  const getTierBadgeClass = (tier) => {
    const classes = {
      free: "badge-free",
      pro: "badge-pro",
      enterprise: "badge-enterprise",
    };
    return `badge ${classes[tier] || ""}`;
  };

  return (
    <div className="min-h-screen bg-gray-100 flex flex-col">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 py-4">
        <div className="max-w-6xl mx-auto px-6 flex justify-between items-center">
          <h1 className="text-2xl font-bold text-gray-900">
            Maidly Business
          </h1>
          <button
            onClick={() => navigate("/user/auth")}
            className="px-4 py-2 border text-sm font-medium transition-colors"
            style={{ borderColor: '#0073ea', color: '#0073ea' }}
          >
            Account Login
          </button>
        </div>
      </div>

      {/* Main Content */}
      <div className="flex-1 p-6">
        <div className="max-w-4xl mx-auto">
          {/* Page Title */}
          <div className="mb-8">
            <button
              data-testid="back-to-home-btn"
              onClick={() => navigate("/")}
              className="text-sm text-gray-600 hover:text-gray-900 mb-4 inline-block"
            >
              ← Back to Home
            </button>
            <h2 className="text-3xl font-bold text-gray-900">
              User Portal
            </h2>
            <p className="text-gray-600">Verify and manage your license</p>
          </div>

        {!licenseData ? (
          /* License Verification Form */
          <Card className="bg-white border border-gray-200 shadow-md">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-xl">
                <Shield className="w-6 h-6" style={{ color: '#0073ea' }} />
                Verify License
              </CardTitle>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleVerifyLicense} className="space-y-4">
                <div>
                  <Label htmlFor="email" className="text-sm font-medium text-gray-700">
                    Email Address
                  </Label>
                  <Input
                    id="email"
                    data-testid="user-email-input"
                    type="email"
                    placeholder="your@email.com"
                    value={formData.user_email}
                    onChange={(e) =>
                      setFormData({ ...formData, user_email: e.target.value })
                    }
                    required
                    className="mt-1 border-gray-300 focus:border-blue-500"
                  />
                </div>

                <div>
                  <Label htmlFor="license" className="text-sm font-medium text-gray-700">
                    License Key
                  </Label>
                  <Input
                    id="license"
                    data-testid="license-key-input"
                    type="text"
                    placeholder="Enter your license key"
                    value={formData.license_key}
                    onChange={(e) =>
                      setFormData({ ...formData, license_key: e.target.value })
                    }
                    required
                    className="mt-1 border-gray-300 font-mono focus:border-blue-500"
                  />
                </div>

                <button
                  data-testid="verify-license-btn"
                  type="submit"
                  disabled={verifying}
                  className="w-full py-4 text-white font-semibold transition-all duration-200"
                  style={{ backgroundColor: '#0073ea' }}
                  onMouseEnter={(e) => e.target.style.backgroundColor = '#005bb5'}
                  onMouseLeave={(e) => e.target.style.backgroundColor = '#0073ea'}
                >
                  {verifying ? "Verifying..." : "Verify License"}
                </button>
              </form>
            </CardContent>
          </Card>
        ) : (
          /* License Status Display */
          <div className="space-y-6">
            <Card className="bg-white border border-gray-200 shadow-md">
              <CardHeader>
                <div className="flex justify-between items-start flex-wrap gap-4">
                  <CardTitle className="flex items-center gap-2 text-xl">
                    {licenseData.status === "active" ? (
                      <CheckCircle className="w-6 h-6 text-green-500" />
                    ) : (
                      <XCircle className="w-6 h-6 text-red-500" />
                    )}
                    License Status
                  </CardTitle>
                  <div className="flex gap-2">
                    <button
                      data-testid="refresh-status-btn"
                      onClick={handleCheckStatus}
                      disabled={loading}
                      className="px-4 py-2 border border-gray-300 text-gray-700 hover:bg-gray-50 text-sm font-medium transition-colors"
                    >
                      {loading ? "Refreshing..." : "Refresh Status"}
                    </button>
                    <button
                      data-testid="user-settings-btn"
                      onClick={() => navigate("/user/settings")}
                      className="px-4 py-2 border text-sm font-medium transition-colors"
                      style={{ borderColor: '#0073ea', color: '#0073ea' }}
                    >
                      Settings
                    </button>
                    <button
                      data-testid="logout-btn"
                      onClick={handleLogout}
                      className="px-4 py-2 bg-red-600 text-white hover:bg-red-700 text-sm font-medium transition-colors"
                    >
                      Logout
                    </button>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="flex gap-2 flex-wrap">
                  <span className={getTierBadgeClass(licenseData.tier)}>
                    {licenseData.tier}
                  </span>
                  <span className={getStatusBadgeClass(licenseData.status)}>
                    {licenseData.status}
                  </span>
                </div>

                <div className="grid sm:grid-cols-2 gap-4">
                  <div className="bg-gray-50 p-4 border border-gray-200">
                    <p className="text-sm text-gray-600 mb-1">Email</p>
                    <p className="font-semibold text-gray-800">{licenseData.user_email}</p>
                  </div>
                  <div className="bg-gray-50 p-4 border border-gray-200">
                    <p className="text-sm text-gray-600 mb-1">Days Remaining</p>
                    <p className="font-semibold text-2xl" style={{ color: '#0073ea' }}>
                      {licenseData.days_remaining}
                    </p>
                  </div>
                </div>

                <div className="bg-gray-50 p-4 border border-gray-200">
                  <p className="text-sm text-gray-600 mb-1">License Key</p>
                  <p className="font-mono text-xs bg-gray-100 p-2 break-all">
                    {licenseData.license_key}
                  </p>
                </div>

                <div className="bg-gray-50 p-4 border border-gray-200">
                  <p className="text-sm text-gray-600 mb-1">Expiration Date</p>
                  <p className="font-semibold text-gray-800">
                    {new Date(licenseData.expires_at).toLocaleDateString("en-US", {
                      year: "numeric",
                      month: "long",
                      day: "numeric",
                    })}
                  </p>
                </div>
              </CardContent>
            </Card>

            {/* Features Card */}
            <Card className="bg-white border border-gray-200 shadow-md">
              <CardHeader>
                <CardTitle className="text-xl">Available Features</CardTitle>
              </CardHeader>
              <CardContent>
                <ul className="grid sm:grid-cols-2 gap-3">
                  {licenseData.features.map((feature) => (
                    <li
                      key={feature}
                      className="flex items-start gap-2 bg-gray-50 p-3 border border-gray-200"
                    >
                      <span style={{ color: '#0073ea' }} className="mt-0.5 font-bold">✓</span>
                      <span className="text-gray-700">{feature}</span>
                    </li>
                  ))}
                </ul>
              </CardContent>
            </Card>
          </div>
        )}
        </div>
      </div>
      
      {/* Footer */}
      <Footer />
    </div>
  );
};

export default UserDashboard;